/**
 * Форматлалт — БҮХ дэлгэц эндээс дуудна.
 * Компонент дотор `toLocaleString()` шууд бичихийг хориглоно.
 *
 * ДҮРЭМ
 *   Мянгат       таслалаар      450,000
 *   Мөнгө        аравтгүй       450,000₮
 *   Ханш         цэгтэй         43.62₮
 *   Тоо ширхэг   тусгаарлагчгүй 42
 *
 * Тайлбар: DESIGN.md §0-д зайгаар тусгаарласан (1 480 000 ₮).
 * Кодод таслалаар шийдсэн. Солих бол ЗӨВХӨН энэ файл.
 */
export const CURRENCY_SIGN = '₮';
/** Decimal | string | number → number. Prisma Decimal-ыг ч хүлээж авна. */
function toNumber(v) {
    if (typeof v === 'number')
        return v;
    if (typeof v === 'string')
        return Number(v);
    if (v && typeof v === 'object' && 'toString' in v)
        return Number(String(v));
    return NaN;
}
function groupThousands(intPart) {
    return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
/**
 * Мөнгө: 450000 → "450,000₮"
 * Аравтын орон харуулахгүй — төгрөгт мөнгө байхгүй.
 */
export function formatMnt(value, opts) {
    const n = toNumber(value);
    if (!Number.isFinite(n))
        return '—';
    const showSign = opts?.sign ?? true;
    const neg = n < 0;
    const rounded = Math.round(Math.abs(n));
    const body = groupThousands(String(rounded));
    return `${neg ? '-' : ''}${body}${showSign ? CURRENCY_SIGN : ''}`;
}
/** Товчилсон: 190400000 → "190.4 сая₮" */
export function formatMntShort(value) {
    const n = toNumber(value);
    if (!Number.isFinite(n))
        return '—';
    const abs = Math.abs(n);
    const sign = n < 0 ? '-' : '';
    if (abs >= 1_000_000_000)
        return `${sign}${(abs / 1_000_000_000).toFixed(1)} тэрбум${CURRENCY_SIGN}`;
    if (abs >= 1_000_000)
        return `${sign}${(abs / 1_000_000).toFixed(1)} сая${CURRENCY_SIGN}`;
    if (abs >= 1_000)
        return `${sign}${groupThousands(String(Math.round(abs)))}${CURRENCY_SIGN}`;
    return `${sign}${Math.round(abs)}${CURRENCY_SIGN}`;
}
/** Рубль: 22950 → "22,950₽" */
export function formatRub(value) {
    const n = toNumber(value);
    if (!Number.isFinite(n))
        return '—';
    return `${groupThousands(String(Math.round(n)))}₽`;
}
/** Тоо ширхэг: 1842 → "1,842". Размер зэрэг жижиг тоонд тусгаарлагч гарахгүй. */
export function formatQty(value) {
    const n = toNumber(value);
    if (!Number.isFinite(n))
        return '—';
    return groupThousands(String(Math.round(n)));
}
/**
 * Размер: 42 → "42", бөглөгдөөгүй бол "—".
 *
 * ⚠️ Оросод олноор бүртгэсэн гутал РАЗМЕРГҮЙ ирнэ (дүрэм 6:
 *    цуглуулагч зөвхөн загвар, өнгө, үнийг мэднэ) — агуулах уншуулах
 *    үедээ бөглөнө. Тэр болтол `items.size` нь NULL байх нь ЗӨВ төлөв.
 *    Түүхий утгыг гаргавал дэлгэц дээр «null» гэж бичигдэж, ажилтан
 *    бүртгэл унасан гэж ойлгоод дахин бүртгэх эрсдэлтэй.
 */
export function formatSize(size) {
    return typeof size === 'number' && Number.isFinite(size) ? String(size) : '—';
}
/**
 * Хэрэглэгчийн оруулсан мөнгийг API-ийн `MoneyString` болгоно
 * (CLAUDE.md №3: мөнгө API-д string-ээр, 2 аравтын оронтой).
 *
 * Хоосон, тоо биш, эсвэл сөрөг бол `'0'` — хүчингүй утга сервер рүү
 * явуулахгүй. Хөнгөлөлт, засварын өртөг, буцаалтын дүнд хэрэглэнэ.
 */
export function moneyInput(value) {
    const n = toNumber(value);
    return Number.isFinite(n) && n > 0 ? n.toFixed(2) : '0';
}
/** Ханш: 43.6234 → "43.62". Аравтын тэмдэг ЦЭГ. */
export function formatRate(value, decimals = 2) {
    const n = toNumber(value);
    if (!Number.isFinite(n))
        return '—';
    return n.toFixed(decimals);
}
/** Дэлгэцэд харуулах серийн: "2600001473" → "2600 0014 73" */
export function formatSerialDisplay(serial) {
    if (!serial)
        return '—';
    const s = serial.replace(/\s/g, '');
    if (s.length !== 10)
        return s;
    return `${s.slice(0, 4)} ${s.slice(4, 8)} ${s.slice(8)}`;
}
/** Утас: "+97699112233" → "+976 9911 2233" */
export function formatPhone(phone) {
    if (!phone)
        return '—';
    const m = /^\+976(\d{4})(\d{4})$/.exec(phone);
    return m ? `+976 ${m[1]} ${m[2]}` : phone;
}
/**
 * Байршил — НЭГ хэлбэрээр: «Салбар 1 › Лангуу» (захиалагчийн цэгцлэл №13).
 *
 * Нэр нь салбарын нэрээр аль хэдийн эхэлдэг бол («Салбар 1 — танхим»)
 * давхардуулахгүй. Салбаргүй байршил (замд, худалдан авагчид) нэрээрээ,
 * байршилгүй бол «—».
 */
export function formatLocation(loc) {
    if (!loc)
        return '—';
    const store = loc.store?.name;
    if (!store || loc.name.startsWith(store))
        return loc.name;
    return `${store} › ${loc.name}`;
}
/** Огноо: Date → "2026.03.14" (Улаанбаатарын цагаар) */
export function formatDate(d) {
    if (!d)
        return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    if (Number.isNaN(date.getTime()))
        return '—';
    const p = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Ulaanbaatar',
        year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(date);
    return p.replace(/-/g, '.');
}
/**
 * Зөвхөн цаг: "09:22" (Улаанбаатарын цагаар).
 *
 * Бүгд нэг өдрийнх гэдэг нь ТОДОРХОЙ үед л хэрэглэнэ (кассын өнөөдрийн
 * жагсаалт) — эс тэгвээс огноогүй цаг төөрөгдүүлнэ.
 */
export function formatTime(d) {
    if (!d)
        return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    if (Number.isNaN(date.getTime()))
        return '—';
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Ulaanbaatar',
        hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(date);
}
/** Огноо + цаг: "2026.03.14 09:22" */
export function formatDateTime(d) {
    if (!d)
        return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    if (Number.isNaN(date.getTime()))
        return '—';
    return `${formatDate(date)} ${formatTime(date)}`;
}
/** Үлдсэн хугацаа: "9 жил 8 сар" / "4 сар" / "Дууссан" */
export function formatRemaining(until, from = new Date()) {
    if (!until)
        return '—';
    const end = typeof until === 'string' ? new Date(until) : until;
    if (Number.isNaN(end.getTime()))
        return '—';
    const ms = end.getTime() - from.getTime();
    if (ms <= 0)
        return 'Дууссан';
    const days = Math.floor(ms / 86_400_000);
    const years = Math.floor(days / 365);
    const months = Math.floor((days % 365) / 30);
    if (years > 0)
        return months > 0 ? `${years} жил ${months} сар` : `${years} жил`;
    if (months > 0)
        return `${months} сар`;
    return `${days} хоног`;
}
/** showPrices=false үед бүх мөнгө нуугдана (танилцуулгын горим, DESIGN.md §6). */
export const MASKED = '•••';
export function maskIfHidden(formatted, showPrices) {
    return showPrices ? formatted : MASKED;
}
//# sourceMappingURL=format.js.map